import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Divider,
  List, ListItem, ListItemText, ListItemButton,
  IconButton, Card, CardContent, Grid, Chip,
  Tooltip, Snackbar, Alert, CircularProgress,
  Badge, LinearProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import AnalysisDialog from './AnalysisDialog';
import { EnglishReviewDialog } from './EnglishReview';
import { API_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import usePracticeLocalization from '../hooks/usePracticeLocalization';
import { localizeScenarioTitle } from '../i18n/scenarios';

// Function to parse query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const History = () => {
  const { tr, isEnglish } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  // Add state variables for AnalysisDialog
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [nodes, setNodes] = useState([]);
  // Assessment report node names/goals/checkpoints in English, keyed by the conversation's scenario
  const reportScenarioId = selectedConversation?.scenarioId || selectedConversation?.scenario_id || null;
  const { translateContent } = usePracticeLocalization(
    reportScenarioId && reportScenarioId !== 'english-app-launch' ? reportScenarioId : null
  );
  const [analysisAutoOpened, setAnalysisAutoOpened] = useState(false);
  const navigate = useNavigate();

  // Get query params
  const query = useQuery();
  const viewConversationId = query.get('view');

  // Initialize the database table on component mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Watch for changes to selectedConversation to auto-open analysis dialog when viewing via URL
  useEffect(() => {
    // If a conversation is selected, it has analysis data, we're viewing via URL, and it hasn't been auto-opened yet
    if (selectedConversation && selectedConversation.analysis && viewConversationId && !analysisOpen && !analysisAutoOpened) {
      // Check if analysis dialog is not already open (redundant check now, but keep for clarity)
      if (!analysisOpen) {
        console.log('Auto-loading analysis for conversation:', viewConversationId);
        // Load analysis data and open dialog
        fetchAnalysisData(selectedConversation.id)
          .then(result => {
            if (result) {
              // setAnalysisOpen(true); // This line opens the dialog
              // setAnalysisAutoOpened(true); // Mark as auto-opened
            }
          })
          .catch(error => {
            console.error('Error auto-loading analysis from selectedConversation change:', error);
          });
      }
    }
  }, [selectedConversation, viewConversationId, analysisOpen, analysisAutoOpened]);

  // Reset auto-open flag when viewConversationId changes
  useEffect(() => {
    setAnalysisAutoOpened(false);
  }, [viewConversationId]);

  // Load specific conversation if viewConversationId is provided
  useEffect(() => {
    if (viewConversationId) {
      // If conversations are already loaded, find the conversation
      if (conversations.length > 0) {
        const conversation = conversations.find(c => c.id === parseInt(viewConversationId));
        if (conversation) {
          setSelectedConversation(conversation);
          // Don't fetch analysis data here - the selectedConversation useEffect will handle it
        } else {
          // If not found in current state, try loading from database directly
          loadSpecificConversation(viewConversationId);
          // Don't fetch analysis data here - the selectedConversation useEffect will handle it
        }
      } else {
        // If conversations not yet loaded, load the specific one directly
        loadSpecificConversation(viewConversationId);
        // Don't fetch analysis data here - the selectedConversation useEffect will handle it
      }
    }
  }, [viewConversationId, conversations]);

  // Function to load a specific conversation by ID from the database
  const loadSpecificConversation = async (id) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/api/storage/conversations/${parseInt(id)}`);
      const result = [response.data];

      if (result && result.length > 0) {
        // Format database result to component format
        const conversation = {
          id: result[0].id,
          scenarioId: result[0].scenario_id,
          scenarioTitle: result[0].scenario_title,
          timestamp: result[0].timestamp,
          messages: typeof result[0].messages === 'string'
            ? JSON.parse(result[0].messages)
            : result[0].messages,
          // Add analysis data if available
          analysis: result[0].has_analysis ? {
            totalScore: result[0].total_score,
            completedCheckpoints: result[0].completed_checkpoints,
            incompleteCheckpoints: result[0].incomplete_checkpoints,
            completionRate: Math.round(Number(result[0].completion_rate) || 0),
            practiceTime: Math.round(Number(result[0].practice_time) || 0)
          } : null
        };
        setSelectedConversation(conversation);
        return true; // Success
      } else {
        setSnackbarMessage(`${tr('找不到對話 ID', 'Conversation not found')}: ${id}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return false; // Failure
      }
    } catch (err) {
      console.error('Error loading conversation by ID:', err);
      setSnackbarMessage(`${tr('載入對話失敗', 'Failed to load conversation')}: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return false; // Failure
    } finally {
      setIsLoading(false);
    }
  };

  // Load conversations from database
  const loadConversations = async () => {
    try {
      setIsLoading(true);
      // Get member_id from localStorage using user object
      const userData = localStorage.getItem('user');
      let memberId = null;

      if (userData) {
        try {
          const user = JSON.parse(userData);
          memberId = user.id;
          console.log('Debug - Current user ID:', memberId); // Debug log
        } catch (error) {
          console.error('Error parsing user data:', error);
          setSnackbarMessage(tr('請先登入以查看歷史對話', 'Please sign in to view conversation history'));
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
          return;
        }
      } else {
        setSnackbarMessage(tr('請先登入以查看歷史對話', 'Please sign in to view conversation history'));
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
      }

      const response = await axios.get(`${API_URL}/api/storage/conversations`, {
        params: { member_id: parseInt(memberId) }
      });
      const dbConversations = response.data;
      setConnectionStatus('Connected to VM database');

      if (dbConversations && dbConversations.length > 0) {
        // Convert db field names (snake_case) to component property names (camelCase)
        const formattedConversations = dbConversations.map(conv => ({
          id: conv.id,
          memberId: conv.member_id,
          scenarioId: conv.scenario_id,
          scenarioTitle: conv.scenario_title,
          timestamp: conv.timestamp,
          messages: typeof conv.messages === 'string' ? JSON.parse(conv.messages) : conv.messages,
          // Add analysis data if available
          analysis: conv.has_analysis ? {
            totalScore: conv.total_score,
            completedCheckpoints: conv.completed_checkpoints,
            incompleteCheckpoints: conv.incomplete_checkpoints,
            completionRate: Math.round(Number(conv.completion_rate) || 0),
            practiceTime: Math.round(Number(conv.practice_time) || 0)
          } : null
        }));
        setConversations(formattedConversations);
        console.log('Debug - Loaded conversations:', formattedConversations.length); // Debug log
      } else {
        setConversations([]);
        loadConversationsFromLocalStorage();
      }
    } catch (err) {
      console.error('Error loading conversations from database:', err);
      setSnackbarMessage(`${tr('載入對話失敗', 'Failed to load conversations')}: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      // Fall back to localStorage if database fails
      loadConversationsFromLocalStorage();
    } finally {
      setIsLoading(false);
    }
  };

  // Load conversations from localStorage as fallback
  const loadConversationsFromLocalStorage = () => {
    const savedConversations = localStorage.getItem('chatHistory');
    if (savedConversations) {
      try {
        const parsedConversations = JSON.parse(savedConversations);
        // Filter conversations by current user's memberId
        const userData = localStorage.getItem('user');
        let memberId = null;

        if (userData) {
          try {
            const user = JSON.parse(userData);
            memberId = user.id;
          } catch (error) {
            console.error('Error parsing user data:', error);
          }
        }

        // Only show conversations for the current user
        const userConversations = memberId
          ? parsedConversations.filter(conv => Number(conv.memberId) === Number(memberId))
          : [];

        setConversations(userConversations);

        // If connected to database, sync localStorage conversations to database
        if (connectionStatus?.includes('Connected')) {
          syncLocalStorageToDatabase(userConversations);
        }
      } catch (error) {
        console.error('解析聊天歷史時出錯:', error);
        setConversations([]);
      }
    }
  };

  // Sync localStorage conversations to database
  const syncLocalStorageToDatabase = async (localConversations) => {
    try {
      // For each conversation in localStorage, insert into database if not exists
      for (const conversation of localConversations) {
        // Ensure we have valid data before inserting
        if (!conversation.id || !conversation.timestamp) {
          console.warn('Skipping invalid conversation object:', conversation);
          continue;
        }

        await axios.put(`${API_URL}/api/storage/conversations/${conversation.id}`, {
          member_id: conversation.memberId ? parseInt(conversation.memberId) : null,
          scenario_id: conversation.scenarioId || '',
          scenario_title: conversation.scenarioTitle || 'Unknown Scenario',
          timestamp: new Date(conversation.timestamp).toISOString(),
          messages: conversation.messages || []
        });
      }

      console.log('Synced localStorage conversations to database');
    } catch (err) {
      console.error('Error syncing localStorage to database:', err);
    }
  };

  const handleSelectConversation = (conversation) => {
    setAnalysisData(null);
    setNodes([]);
    setSelectedConversation(conversation);
  };

  const handleDeleteConversation = async (index, event) => {
    event.stopPropagation();

    try {
      const conversationToDelete = conversations[index];
      // Use the id property directly - it exists in both localStorage and database objects
      const conversationId = conversationToDelete.id;

      // Delete from database if connected
      if (connectionStatus?.includes('Connected')) {
        setIsLoading(true);
        await axios.delete(`${API_URL}/api/storage/conversations/${conversationId}`);
      }

      // Update local state
      const updatedConversations = [...conversations];
      updatedConversations.splice(index, 1);
      setConversations(updatedConversations);

      // Update localStorage
      localStorage.setItem('chatHistory', JSON.stringify(updatedConversations));

      // Show success message
      setSnackbarMessage(tr('對話已刪除', 'Conversation deleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Reset selected conversation if deleted
      if (selectedConversation && selectedConversation.id === conversationId) {
        setSelectedConversation(null);
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setSnackbarMessage(`${tr('刪除失敗', 'Delete failed')}: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToList = () => {
    setAnalysisOpen(false);
    setAnalysisData(null);
    setNodes([]);
    setSelectedConversation(null);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString(isEnglish ? 'en-US' : 'zh-TW', {
      timeZone: 'Asia/Taipei'
    });
  };

  const getScenarioName = (conversation) => {
    // Saved titles are the original Chinese scenario titles; show the English copy in the English UI
    const title = conversation.scenarioTitle || conversation.scenario_title;
    if (!title) return tr('未知場景', 'Unknown Scenario');
    return localizeScenarioTitle(title, conversation.scenarioId || conversation.scenario_id, isEnglish);
  };

  const isEnglishConversation = (conversation) => (
    conversation?.scenarioId === 'english-app-launch'
    || conversation?.scenario_id === 'english-app-launch'
  );

  const getPartnerLabel = (conversation) => (
    isEnglishConversation(conversation) ? 'Alex' : 'Customer'
  );

  const handleCopyConversation = () => {
    if (!selectedConversation) return;

    const formattedMessages = selectedConversation.messages.map(message => {
      const sender = message.sender === 'user' ? 'You' : getPartnerLabel(selectedConversation);
      const time = formatDate(message.timestamp);
      return `${sender} (${time}): ${message.text}`;
    }).join('\n\n');

    const conversationInfo = `Scenario: ${getScenarioName(selectedConversation)}\nDate: ${formatDate(selectedConversation.timestamp)}\n\n`;

    const fullText = conversationInfo + formattedMessages;

    navigator.clipboard.writeText(fullText)
      .then(() => {
        setSnackbarMessage(tr('對話已複製到剪貼簿', 'Conversation copied to clipboard'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Failed to copy conversation:', err);
        setSnackbarMessage('Failed to copy conversation');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Function to handle clicking "View Analysis" button
  const handleViewAnalysis = async () => {
    if (!selectedConversation) return;

    try {
      // Fetch analysis data for the selected conversation
      const result = await fetchAnalysisData(selectedConversation.id);

      if (result) {
        // Open the analysis dialog
        setAnalysisOpen(true);
      }
    } catch (error) {
      console.error('Error viewing analysis:', error);
      setSnackbarMessage(`${tr('無法查看分析', 'Unable to view analysis')}: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Function to format practice time
  const formatPracticeTime = (seconds) => {
    if (!seconds) return '00:00';
    return `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  // Function to fetch analysis data for a conversation
  const fetchAnalysisData = async (conversationId) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_URL}/api/storage/conversations/${parseInt(conversationId)}/analysis`
      );
      const result = [response.data];

      if (result && result.length > 0) {
        // Get the analysis data
        let analysisJson = result[0].analysis_data;
        try {
          // Parse JSON if it's a string
          if (typeof analysisJson === 'string') {
            analysisJson = JSON.parse(analysisJson);
          }
          setAnalysisData(analysisJson);

          // Get nodes data if available
          let nodesData = result[0].nodes_data;
          if (nodesData) {
            // Parse JSON if it's a string
            if (typeof nodesData === 'string') {
              nodesData = JSON.parse(nodesData);
            }
            console.log('Loaded nodes data:', nodesData);
            setNodes(nodesData);
          } else {
            console.log('No nodes data available for this analysis');
            setNodes([]);
          }

          // Ensure practice_time is an integer
          const practiceTime = Math.round(Number(result[0].practice_time) || 0);

          return {
            analysisData: analysisJson,
            practiceTime: practiceTime,
            nodesData: nodesData || []
          };
        } catch (error) {
          console.error('Error parsing analysis data:', error);
          throw new Error('Invalid analysis data format');
        }
      } else {
        throw new Error('No analysis data found for this conversation');
      }
    } catch (err) {
      console.error('Error fetching analysis data:', err);
      setSnackbarMessage(`${tr('載入分析數據失敗', 'Failed to load analysis data')}: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        {tr('歷史對話', 'Conversation History')}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {connectionStatus && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography
            color={connectionStatus.includes('failed') ? 'error' : 'success.main'}
            variant="body2"
          >
            {tr('已連線至 VM 資料庫', connectionStatus)}
          </Typography>
        </Box>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : selectedConversation ? (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={handleBackToList}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {getScenarioName(selectedConversation)} - {formatDate(selectedConversation.timestamp)}
            </Typography>
            <Tooltip title={tr('複製對話', 'Copy conversation')}>
              <IconButton onClick={handleCopyConversation} aria-label="copy conversation">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Analysis summary if available */}
          {selectedConversation.analysis && (
            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f7fa 100%)',
                borderLeft: '4px solid #3b82f6'
              }}
            >
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AssessmentIcon color="primary" />
                    {isEnglishConversation(selectedConversation)
                      ? tr('英文能力講評', 'English Skills Review')
                      : tr('評估報告', 'Assessment Report')}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">{tr('總得分', 'Total Score')}</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary.main">
                        {selectedConversation.analysis.totalScore}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">{tr('完成度', 'Completion')}</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary.main">
                        {selectedConversation.analysis.completionRate}%
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">{tr('練習時間', 'Practice Time')}</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary.main">
                        {formatPracticeTime(selectedConversation.analysis.practiceTime)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      {isEnglishConversation(selectedConversation)
                        ? tr('完成任務', 'Tasks Completed')
                        : tr('達成查核點', 'Checkpoints Met')}
                    </Typography>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: 'rgba(76, 175, 80, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: 'success.main',
                      fontSize: '1.25rem'
                    }}>
                      {selectedConversation.analysis.completedCheckpoints}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      {isEnglishConversation(selectedConversation)
                        ? tr('未完成任務', 'Tasks Incomplete')
                        : tr('未達成查核點', 'Checkpoints Not Met')}
                    </Typography>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: 'rgba(244, 67, 54, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: 'error.main',
                      fontSize: '1.25rem'
                    }}>
                      {selectedConversation.analysis.incompleteCheckpoints}
                    </Box>
                  </Box>

                  <Box sx={{ ml: 2 }}>
                    <Tooltip title={tr('查看完整評估報告', 'View full assessment report')}>
                      <IconButton
                        color="primary"
                        onClick={handleViewAnalysis}
                        aria-label="view analysis"
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          }
                        }}
                      >
                        <AssessmentIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}

          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Chip
                label={`${tr('場景', 'Scenario')}: ${getScenarioName(selectedConversation)}`}
                sx={{ mr: 1, mb: 1 }}
              />
              <Chip
                label={`${tr('日期', 'Date')}: ${formatDate(selectedConversation.timestamp)}`}
                sx={{ mr: 1, mb: 1 }}
              />
              <Chip
                label={`${tr('訊息數', 'Messages')}: ${selectedConversation.messages.length}`}
                sx={{ mr: 1, mb: 1 }}
              />
            </Box>

            <Box sx={{ maxHeight: '60vh', overflow: 'auto', p: 2 }}>
              {selectedConversation.messages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    mb: 2
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      backgroundColor: message.sender === 'user' ? '#e3f2fd' : '#f5f5f5',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      {message.sender === 'user' ? tr('你', 'You') : getPartnerLabel(selectedConversation)} - {formatDate(message.timestamp)}
                    </Typography>
                    <Typography variant="body1">
                      {message.text}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {conversations.length > 0 ? (
            conversations.map((conversation, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 6 },
                    position: 'relative',
                    ...(conversation.analysis && {
                      borderLeft: '4px solid #3b82f6'
                    })
                  }}
                  onClick={() => handleSelectConversation(conversation)}
                >
                  {/* Position delete button absolutely at top-right corner */}
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteConversation(index, e)}
                    aria-label="delete conversation"
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      zIndex: 2,
                      bgcolor: 'rgba(255,255,255,0.7)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>

                  {/* Move score chip to left side of card */}
                  {conversation.analysis && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        zIndex: 1
                      }}
                    >
                      <Chip
                        size="small"
                        label={`${tr('分數', 'Score')}: ${conversation.analysis.totalScore}`}
                        color="primary"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  )}

                  <CardContent sx={{ pt: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Typography variant="h6" noWrap sx={{ width: '100%' }}>
                        {getScenarioName(conversation)}
                      </Typography>
                    </Box>
                    <Typography color="textSecondary">
                      {formatDate(conversation.timestamp)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {conversation.messages.length} {tr('則訊息', 'messages')}
                    </Typography>

                    {conversation.analysis && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            {tr('完成度', 'Completion')}: {conversation.analysis.completionRate}%
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                            <Typography variant="caption">
                              {conversation.analysis.completedCheckpoints} / {conversation.analysis.completedCheckpoints + conversation.analysis.incompleteCheckpoints}
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={conversation.analysis.completionRate}
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                      </Box>
                    )}

                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {conversation.messages[0]?.text || tr('沒有訊息', 'No messages')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                  {tr('找不到歷史對話', 'No conversation history found')}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {tr('開始新的對話後，紀錄會顯示在這裡', 'Start a new conversation to see it here')}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <AnalysisDialog
        open={analysisOpen && analysisData?.kind !== 'english_practice_review'}
        onClose={() => setAnalysisOpen(false)}
        data={analysisData}
        nodes={nodes}
        translateContent={translateContent}
        practice_time={selectedConversation?.analysis?.practiceTime ? Math.round(Number(selectedConversation.analysis.practiceTime)) : 0}
        conversationId={selectedConversation?.id}
      />
      <EnglishReviewDialog
        open={analysisOpen && analysisData?.kind === 'english_practice_review'}
        onClose={() => setAnalysisOpen(false)}
        review={analysisData}
      />
    </Container>
  );
};

export default History;
